var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import DebugService from './DebugService';
import { detectFileProtectionFromBase64, isSupportedFileType } from '../utils/fileInspector';
class FileValidationService {
    constructor() {
        // Supported file extensions
        this.supportedExtensions = [
            '.bat', '.bashc', '.c', '.cc', '.chtml', '.cmake', '.cmd', '.cpp',
            '.cs', '.css', '.csv', '.cxx', '.cy', '.dockerfile', '.doc', '.docx',
            '.eml', '.gdoc', '.go', '.gsheet', '.gslides', '.h++', '.hpp', '.html',
            '.java', '.jpeg', '.jpg', '.js', '.json', '.mht', '.mhtml', '.mkfile',
            '.msg', '.pdf', '.perl', '.php', '.plsql', '.png', '.pptx', '.py',
            '.pxi', '.pyx', '.r', '.rd', '.rs', '.rtf', '.ruby', '.tif', '.tiff',
            '.ts', '.txt', '.xls', '.xlsx', '.xml', '.yaml', '.yml', '.zsh', '.zip'
        ];
        // Compressed file extensions
        this.compressedExtensions = [
            '.rar', '.7z', '.tar', '.gz', '.bz2', '.xz', '.tar.gz', '.tar.bz2'
        ];
    }
    static getInstance() {
        if (!FileValidationService.instance) {
            FileValidationService.instance = new FileValidationService();
        }
        return FileValidationService.instance;
    }
    /**
     * Validate email attachments for various file restrictions
     * @param item Office.js mailbox item
     * @returns Promise<FileValidationResult>
     */
    validateEmailAttachments(item) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                DebugService.debug('Starting file validation for email attachments');
                const errors = [];
                // Get attachments from the email
                const attachments = yield this.getEmailAttachments(item);
                DebugService.debug(`Found ${attachments.length} attachments to validate`);
                if (attachments.length === 0) {
                    return { isValid: true, errors: [] };
                }
                // Check for compressed files
                /*const zipFiles = attachments.filter(att => this.isCompressedFile(att.name));
                DebugService.debug(`Found ${zipFiles.length} compressed files:`, zipFiles.map(f => f.name));
                if (zipFiles.length > 0) {
                  errors.push({
                    type: 'zip',
                    message: 'One or multiple .zip files attached. Please remove all .zip file(s) to submit.',
                    files: zipFiles.map(f => f.name)
                  });
                  DebugService.debug('Added zip validation error');
                }*/
                // Check for unsupported file types
                const unsupportedFiles = attachments.filter(att => !this.isSupportedFile(att.name));
                if (unsupportedFiles.length > 0) {
                    errors.push({
                        type: 'unsupported',
                        message: `One or more unsupported file types attached. Please remove unsupported file(s) to submit.`,
                        files: unsupportedFiles.map(f => f.name)
                    });
                }
                // Check for encrypted files (basic detection)
                const encryptedFiles = yield this.detectEncryptedFiles(attachments);
                if (encryptedFiles.length > 0) {
                    errors.push({
                        type: 'encrypted',
                        message: 'One or more files is encrypted. Please remove encrypted file(s) to submit.',
                        files: encryptedFiles
                    });
                }
                // Check for password protected files (basic detection)
                const passwordProtectedFiles = yield this.detectPasswordProtectedFiles(attachments);
                if (passwordProtectedFiles.length > 0) {
                    errors.push({
                        type: 'password_protected',
                        message: 'One or more files is password protected. Please remove password protected file(s) to submit.',
                        files: passwordProtectedFiles
                    });
                }
                const isValid = errors.length === 0;
                DebugService.debug(`File validation completed. Valid: ${isValid}, Errors: ${errors.length}`);
                return { isValid, errors };
            }
            catch (error) {
                DebugService.error('File validation failed');
                // Return a generic error if validation fails
                return {
                    isValid: false,
                    errors: [{
                            type: 'unsupported',
                            message: 'Unable to validate attachments. Please check file types and try again.',
                            files: []
                        }]
                };
            }
        });
    }
    /**
     * Get email attachments from Office.js item with file content for analysis
     */
    getEmailAttachments(item) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve) => {
                try {
                    DebugService.debug('Getting email attachments with content, item type');
                    // For compose mode, try to get attachments
                    if (item.attachments && typeof item.attachments.getAsync === 'function') {
                        DebugService.debug('Using compose mode attachment access');
                        item.attachments.getAsync((result) => __awaiter(this, void 0, void 0, function* () {
                            if (result.status === Office.AsyncResultStatus.Succeeded) {
                                const attachments = result.value || [];
                                DebugService.debug(`Compose mode: Found ${attachments.length} attachments`);
                                // Get attachment content for password protection detection
                                const attachmentsWithContent = yield this.getAttachmentContent(item, attachments);
                                resolve(attachmentsWithContent);
                            }
                            else {
                                DebugService.warn('Failed to get attachments in compose mode');
                                resolve([]);
                            }
                        }));
                    }
                    else if (item.itemType === Office.MailboxEnums.ItemType.Message) {
                        // For read mode, try to access attachments directly
                        DebugService.debug('Using read mode attachment access');
                        if (item.attachments && Array.isArray(item.attachments)) {
                            DebugService.debug(`Read mode: Found ${item.attachments.length} attachments directly`);
                            // Get attachment content for password protection detection
                            this.getAttachmentContent(item, item.attachments).then(resolve);
                        }
                        else {
                            DebugService.warn('No attachments found in read mode');
                            resolve([]);
                        }
                    }
                    else {
                        DebugService.warn('Unknown item type or no attachment access method');
                        resolve([]);
                    }
                }
                catch (error) {
                    DebugService.error('Error getting email attachments');
                    resolve([]);
                }
            });
        });
    }
    /**
     * Get attachment content for password protection detection
     */
    getAttachmentContent(item, attachments) {
        return __awaiter(this, void 0, void 0, function* () {
            const attachmentsWithContent = [];
            for (const attachment of attachments) {
                const attachmentData = {
                    name: attachment.name || 'Unknown',
                    size: attachment.size || 0,
                    contentType: attachment.contentType || 'application/octet-stream',
                    content: undefined
                };
                // Try to get attachment content for password protection detection
                DebugService.debug(`Attempting to get content for attachment: ${attachment.name}, ID: ${attachment.id}`);
                if (typeof item.getAttachmentContentAsync === 'function' && attachment.id) {
                    try {
                        DebugService.debug(`Calling getAttachmentContentAsync for: ${attachment.name}`);
                        const contentResult = yield new Promise((resolve) => {
                            item.getAttachmentContentAsync(attachment.id, { asyncContext: attachment.id }, (result) => {
                                DebugService.debug(`getAttachmentContentAsync callback`);
                                resolve(result);
                            });
                        });
                        if (contentResult.status === Office.AsyncResultStatus.Succeeded && contentResult.value) {
                            if (contentResult.value.format === Office.MailboxEnums.AttachmentContentFormat.Base64) {
                                attachmentData.content = contentResult.value.content;
                                DebugService.debug(`Retrieved content for attachment: ${attachment.name} (${contentResult.value.content.length} chars)`);
                            }
                            else {
                                DebugService.debug(`Attachment ${attachment.name} content format not supported: ${contentResult.value.format}`);
                            }
                        }
                        else {
                            DebugService.warn('Failed to get content for attachment');
                        }
                    }
                    catch (error) {
                        DebugService.warn('Error getting content for attachment');
                    }
                }
                else {
                    DebugService.debug(`Cannot get content for attachment`);
                }
                attachmentsWithContent.push(attachmentData);
            }
            return attachmentsWithContent;
        });
    }
    /**
     * Check if file is compressed
     */
    isCompressedFile(filename) {
        const extension = this.getFileExtension(filename).toLowerCase();
        return this.compressedExtensions.includes(extension);
    }
    /**
     * Check if file type is supported
     */
    isSupportedFile(filename) {
        const extension = this.getFileExtension(filename).toLowerCase();
        return this.supportedExtensions.includes(extension);
    }
    /**
     * Get file extension from filename
     */
    getFileExtension(filename) {
        const lastDot = filename.lastIndexOf('.');
        return lastDot !== -1 ? filename.substring(lastDot) : '';
    }
    /**
     * Detect encrypted files including M365 encryption and common encryption patterns
     */
    detectEncryptedFiles(attachments) {
        return __awaiter(this, void 0, void 0, function* () {
            const encryptedFiles = [];
            for (const attachment of attachments) {
                const filename = attachment.name.toLowerCase();
                const contentType = attachment.contentType.toLowerCase();
                // Check for explicit encrypted file extensions
                if (filename.includes('.encrypted') ||
                    filename.includes('.enc') ||
                    filename.includes('.gpg') ||
                    filename.includes('.pgp') ||
                    filename.includes('.asc') ||
                    filename.includes('.key')) {
                    encryptedFiles.push(attachment.name);
                    continue;
                }
                // Check for encrypted content types
                if (contentType === 'application/pgp-encrypted' ||
                    contentType === 'application/pkcs7-mime' ||
                    contentType === 'application/x-pkcs7-mime' ||
                    contentType === 'application/octet-stream' && filename.includes('encrypted')) {
                    encryptedFiles.push(attachment.name);
                    continue;
                }
                // Check for M365 encryption indicators
                // M365 encrypted files often have specific content types or size patterns
                if (this.isM365EncryptedFile(attachment)) {
                    encryptedFiles.push(attachment.name);
                    continue;
                }
                // Check for Office document encryption (password-protected files that are also encrypted)
                if (this.isOfficeEncryptedFile(attachment)) {
                    encryptedFiles.push(attachment.name);
                }
            }
            return encryptedFiles;
        });
    }
    /**
     * Check if file appears to be M365 encrypted
     * M365 encryption is complex and may not always be detectable via simple patterns
     */
    isM365EncryptedFile(attachment) {
        const filename = attachment.name.toLowerCase();
        const contentType = attachment.contentType.toLowerCase();
        // Only check for explicit M365 encryption indicators to avoid false positives
        // Check for M365-specific encrypted content types
        if (contentType === 'application/x-microsoft-encrypted' ||
            contentType === 'application/x-microsoft-office-encrypted') {
            return true;
        }
        // Check for files with explicit encryption indicators in filename
        if (filename.includes('encrypted') && (filename.endsWith('.encrypted') ||
            filename.includes('_encrypted') ||
            filename.includes('_enc_'))) {
            return true;
        }
        return false;
    }
    /**
     * Check if Office document appears to be encrypted (password-protected)
     */
    isOfficeEncryptedFile(attachment) {
        const filename = attachment.name.toLowerCase();
        const contentType = attachment.contentType.toLowerCase();
        // Check for Office document types that might be encrypted
        const officeExtensions = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.pdf'];
        const isOfficeFile = officeExtensions.some(ext => filename.endsWith(ext));
        if (!isOfficeFile)
            return false;
        // Check for encrypted Office content types
        if (contentType === 'application/x-microsoft-office-encrypted' ||
            contentType === 'application/x-password-protected') {
            return true;
        }
        return false;
    }
    /**
     * Detect password protected files including Office documents (PDF, Excel, Word, PowerPoint)
     * Now uses file content analysis for more accurate detection
     */
    detectPasswordProtectedFiles(attachments) {
        return __awaiter(this, void 0, void 0, function* () {
            const passwordProtectedFiles = [];
            for (const attachment of attachments) {
                const filename = attachment.name.toLowerCase();
                const contentType = attachment.contentType.toLowerCase();
                // First, try content-based detection if content is available
                DebugService.debug(`Processing attachment: ${attachment.name}, has content: ${!!attachment.content}, supported: ${isSupportedFileType(attachment.name)}`);
                if (attachment.content && isSupportedFileType(attachment.name)) {
                    try {
                        DebugService.debug(`Starting comprehensive content analysis for: ${attachment.name}`);
                        DebugService.debug(`Content length: ${attachment.content.length} characters`);
                        DebugService.debug(`Content preview: ${attachment.content.substring(0, 50)}...`);
                        const result = yield detectFileProtectionFromBase64(attachment.content);
                        if (result.encrypted) {
                            passwordProtectedFiles.push(attachment.name);
                            DebugService.debug(`Comprehensive analysis detected password protection: ${attachment.name} - ${result.details.join(', ')}`);
                            continue;
                        }
                        else {
                            DebugService.debug(`Comprehensive analysis found no password protection: ${attachment.name}`);
                        }
                    }
                    catch (error) {
                        DebugService.warn('Error in comprehensive analysis for password protection');
                    }
                }
                else {
                    DebugService.debug(`Skipping comprehensive analysis for ${attachment.name} - content: ${!!attachment.content}, supported: ${isSupportedFileType(attachment.name)}`);
                }
                // Fallback to filename and content type based detection
                // Check for explicit password protection indicators in filename
                if (filename.includes('password') ||
                    filename.includes('protected') ||
                    filename.includes('locked') ||
                    filename.includes('secure') ||
                    filename.includes('encrypted') ||
                    filename.includes('_pw_') ||
                    filename.includes('_pass_')) {
                    passwordProtectedFiles.push(attachment.name);
                    DebugService.debug(`Filename analysis detected password protection: ${attachment.name}`);
                    continue;
                }
                // Check for password-protected content types
                if (contentType === 'application/x-password-protected' ||
                    contentType === 'application/x-microsoft-office-encrypted' ||
                    contentType.includes('password') ||
                    contentType.includes('protected')) {
                    passwordProtectedFiles.push(attachment.name);
                    DebugService.debug(`Content type analysis detected password protection: ${attachment.name}`);
                    continue;
                }
                // Check for Office documents that might be password-protected (fallback)
                if (this.isOfficePasswordProtectedFile(attachment)) {
                    passwordProtectedFiles.push(attachment.name);
                    DebugService.debug(`Office file analysis detected password protection: ${attachment.name}`);
                    continue;
                }
                // Check for PDF files that might be password-protected (fallback)
                if (this.isPasswordProtectedPDF(attachment)) {
                    passwordProtectedFiles.push(attachment.name);
                    DebugService.debug(`PDF analysis detected password protection: ${attachment.name}`);
                }
            }
            return passwordProtectedFiles;
        });
    }
    /**
     * Check if Office document (Word, Excel, PowerPoint) appears to be password-protected
     */
    isOfficePasswordProtectedFile(attachment) {
        const filename = attachment.name.toLowerCase();
        const contentType = attachment.contentType.toLowerCase();
        // Office document extensions
        const officeExtensions = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];
        const isOfficeFile = officeExtensions.some(ext => filename.endsWith(ext));
        if (!isOfficeFile)
            return false;
        // Check for encrypted Office content types
        if (contentType === 'application/x-microsoft-office-encrypted' ||
            contentType === 'application/x-password-protected') {
            return true;
        }
        return false;
    }
    /**
     * Check if PDF file appears to be password-protected
     */
    isPasswordProtectedPDF(attachment) {
        const filename = attachment.name.toLowerCase();
        const contentType = attachment.contentType.toLowerCase();
        if (!filename.endsWith('.pdf'))
            return false;
        // Check for encrypted PDF content types
        if (contentType === 'application/x-password-protected' ||
            contentType === 'application/pdf-encrypted') {
            return true;
        }
        return false;
    }
    /**
     * Get the primary error message to display to user
     */
    getPrimaryErrorMessage(errors) {
        if (errors.length === 0)
            return null;
        // Priority order: unsupported > encrypted > password protected
        const priorityOrder = ['unsupported', 'encrypted', 'password_protected'];
        for (const type of priorityOrder) {
            const error = errors.find(e => e.type === type);
            if (error) {
                return error.message;
            }
        }
        return errors[0].message;
    }
    /**
     * Get all error messages combined for display to user
     * This shows all validation errors at once instead of one at a time
     */
    getAllErrorMessages(errors) {
        if (errors.length === 0)
            return null;
        // Always use the structured format, even for single errors
        const errorMessages = [];
        // Add main error message
        errorMessages.push('There\'s one or more unsupported file types attached.');
        // Track files that have already been shown to avoid duplicates
        const shownFiles = new Set();
        // Priority order: unsupported > encrypted > password protected
        const priorityOrder = ['unsupported', 'encrypted', 'password_protected'];
        // Add specific file details for each error type in priority order
        priorityOrder.forEach(errorType => {
            const error = errors.find(e => e.type === errorType);
            if (error && error.files.length > 0) {
                // Filter out files that have already been shown
                const newFiles = error.files.filter(file => !shownFiles.has(file));
                if (newFiles.length > 0) {
                    const errorTypeLabel = this.getErrorTypeLabel(error.type);
                    const fileList = newFiles.join(', ');
                    errorMessages.push(`• ${errorTypeLabel}: ${fileList}`);
                    // Mark these files as shown
                    newFiles.forEach(file => shownFiles.add(file));
                }
            }
        });
        return errorMessages.join('\n');
    }
    /**
     * Get user-friendly label for error type
     */
    getErrorTypeLabel(errorType) {
        switch (errorType) {
            /*case 'zip':
              return 'Zip files';*/
            case 'unsupported':
                return 'Unsupported file';
            case 'encrypted':
                return 'Encrypted file';
            case 'password_protected':
                return 'Password protected file';
            default:
                return 'Invalid file';
        }
    }
    /**
     * Get detailed error information for debugging
     * This helps understand what types of files were detected as problematic
     */
    getDetailedErrorInfo(errors) {
        if (errors.length === 0)
            return 'No validation errors';
        const details = errors.map(error => {
            const fileList = error.files.length > 0 ? ` (${error.files.join(', ')})` : '';
            return `${error.type}: ${error.message}${fileList}`;
        });
        return details.join('; ');
    }
    /**
     * Get validation statistics for monitoring
     */
    getValidationStats(result) {
        const totalFiles = result.errors.reduce((sum, error) => sum + error.files.length, 0);
        const validFiles = totalFiles - result.errors.reduce((sum, error) => sum + error.files.length, 0);
        const errorCounts = {};
        result.errors.forEach(error => {
            errorCounts[error.type] = (errorCounts[error.type] || 0) + error.files.length;
        });
        return {
            totalFiles,
            validFiles,
            errorCounts
        };
    }
}
export default FileValidationService.getInstance();
